import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { catchError, filter, map, Observable, of, Subject, Subscription, switchMapTo, take, tap } from 'rxjs';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ReportsService } from '../../services/reports.service';
import { ReportListItemModel } from '../../models/report-list-item.model';
import { TableColumnModel } from '../../../Forms/models/table-column.model';
import { ReportBlockTypeEnum, reportChartInfoMap } from '../../enums/report-block-type.enum';
import { ChartOptionModel } from '../../models/chart-option.model';

import { SeriesPickerFieldModel } from '../../models/series-picker-field.model';
import { SeriesPickerCompareFieldModel } from '../../models/series-picker-compare-field.model';
import { ReportSeriesModel } from '../../models/report-series.model';
import { DateRangeFieldModel } from '../../models/date-range-field.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { BlockModel } from '../../../PageBuilder/models/block.model';
import { coverageOverTimeComparisonBlockType } from '../../models/reports-block-types.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { ReportDateRangeModel } from '../../models/report-date-range.model';
import { UserAppPreferenceChartColors } from '../../../../Core/Users/models/user-app-preferences-chart-colors.model';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { setDefaultSeriesColorConfig } from '../../../Charts/config/default-series-color.config';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { ReportsRoutingService } from '../../../../Reports/Core/services/reports-routing.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { FocusHelper } from '../../../Forms/helpers/focus.helper';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { defaultDateTypeOptions, savedSourceDateTypeOptions } from '../../../Forms/components/DateRangeSelector/date-range-selector.component';
import { reportSavedSourceTypes } from '../SeriesPickerField/series-picker-field.component';
import { DimensionsModel } from '../../../../Common/Misc/models/dimensions.model';


enum ReportCreateDialogTabsEnum {
    QUICK_REPORT,
    BLANK_REPORT,
    FROM_PREVIOUS
};

@Component({
    selector: 'agpr-report-create-dialog',
    templateUrl: './report-create-dialog.component.html',
    styleUrls: ['./report-create-dialog.component.scss'],
    standalone: false
})
export class ReportCreateDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    public subscriptions: Subscription[] = [];
    public createQuickReport: boolean = true;
    public createFromPrevious: boolean = null;
    public createCustomReport: boolean;
    public createSingleSeriesReport: boolean = true;
    public selectedReport: ReportListItemModel = null;
    public savedReportFilterText: string = '';
    public secondStep: boolean = false;

    public reportListItems: ReportListItemModel[];
    public rowData: ReportListItemModel[] = [];
    public reportColumnDefs: TableColumnModel[] = [
        {
            field: 'name',
            width: 500,
        },
    ];
    public options: ChartOptionModel[] = defaultChartOptionValuesModel;
    public selectedCharts: ChartOptionModel[] = [];
    public multiSeriesChartLabels: string[] = [];
    public singleSeriesChartLabels: string[] = [];
    public reportTitle: string;
    public singleSeriesField: SeriesPickerFieldModel = new SeriesPickerFieldModel(
        null,
        'singleSeries',
        'Reports.quickReport.chart.singleSeries.label',
        '',
        false,
        undefined,
        undefined,
        undefined,
        this.singleSeriesChartLabels,
        false,
        false,
        true
    );
    public multiSeriesField: SeriesPickerCompareFieldModel = new SeriesPickerCompareFieldModel(
        null,
        'multiSeries',
        'Reports.quickReport.chart.multiSeries.label',
        '',
        undefined,
        this.multiSeriesChartLabels,
        false,
        false,
        true
    );
    public dateRangeField: DateRangeFieldModel = new DateRangeFieldModel(
        null,
        'date',
        'Reports.formField.dateRange.label',
        ''
    );
    public chartDimensionsMap: Map<ReportBlockTypeEnum, DimensionsModel> = new Map<ReportBlockTypeEnum, DimensionsModel>([
        [ReportBlockTypeEnum.COVERAGE_SUMMARY, new DimensionsModel(3, 2)],
        [ReportBlockTypeEnum.COVERAGE_OVER_TIME, new DimensionsModel(3, 7)],
        [ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN, new DimensionsModel(3, 4)],
        [ReportBlockTypeEnum.SENTIMENT_TREND, new DimensionsModel(3, 5)],
        [ReportBlockTypeEnum.SENTIMENT_BREAKDOWN, new DimensionsModel(3, 4)],
        [ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME, new DimensionsModel(3, 5)],
        [ReportBlockTypeEnum.TOP_10_OUTLETS, new DimensionsModel(4, 3)],
        [ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME, new DimensionsModel(4, 3)],
        [ReportBlockTypeEnum.TOP_HASHTAGS, new DimensionsModel(4, 3)],
    ]);
    public series: ReportSeriesModel[] = [new ReportSeriesModel()];
    public singleSeries: ReportSeriesModel[] = [new ReportSeriesModel()];
    public multiSeries: ReportSeriesModel[] = [new ReportSeriesModel()];
    public heading: string = 'Reports.quickReportDialog.quickReportSetup';
    public validReportTitle: boolean = true;
    public validSeries: boolean = true;
    public validMultiSeries: boolean = true;
    public validDateRange: boolean = true;
    public messageType = messageSeverityEnum;
    public singleSeriesBlock: BlockModel = new BlockModel();
    public multiSeriesBlock: BlockModel = new BlockModel(undefined, coverageOverTimeComparisonBlockType);
    public multiSeriesChartSelected: boolean;
    public singleSeriesChartSelected: boolean;
    public hasDateRangeError: boolean;
    public showQuickReportOption: boolean = true;
    public dateTypeOptions: DropdownOptionModel[] = defaultDateTypeOptions;
    public selectedTabIndex: number = 0;

    readonly multiOptionsCount: number = defaultChartOptionValuesModel.filter(option => option.multiSeries).length;
    readonly singleOptionsCount: number = defaultChartOptionValuesModel.filter(option => !option.multiSeries).length;
    readonly buttonTypes = ButtonTypesEnum;

    private userChartColors$: Observable<UserAppPreferenceChartColors>;
    private createNewQuickReport$ = new Subject<void>();

    showError: boolean = false;
    timezone: string;

    constructor(
        public coreDialogService: CoreDialogService,
        private reportsService: ReportsService,
        private reportsRoutingService: ReportsRoutingService,
        private userPreferencesService: UserPreferencesService,
        private currentUserStateService: CurrentUserStateService,
        private el: ElementRef
    ) {
        super(coreDialogService, 'ReportCreate');
    }
    ngOnInit(): void {
        this.userChartColors$ = this.userPreferencesService.getUserAppPreferences().pipe(
            map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
            catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors)),
            tap(chartColors => setDefaultSeriesColorConfig(chartColors.generalColors))
        );
        this.subscriptions.push(
            this.reportsService.getReportListItems().subscribe(
                reports => {
                    this.reportListItems = reports.slice().sort((a, b) => {
                        return +b.createdDate - +a.createdDate;
                    });
                    this.setFilteredReports();
                }
            ),
            this.userChartColors$.subscribe(),
            this.currentUserStateService.getPermissions().pipe(
                tap(permissions => {
                    if (!permissions.some(p => p === userPermissionsEnum.advancedMonitoring)) {
                        this.setCreateQuickReport(false);
                        this.setCreateFromPrevious(false);
                        this.showQuickReportOption = false;
                    }

                    this.options = this.options.filter(option => permissions.some(p => option.hasPermission(p)));
                })
            ).subscribe(),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    this.timezone = preferences?.timeZone;
                }
            ),
            this.createNewQuickReport$.pipe(
                map(() => {
                    // @HACK Fixes a bug that if you click "create report" while the date filter
                    // calendar is open, then the dates you picked will not be selected
                    if (this.isDatePickerOpen()) {
                        requestAnimationFrame(() => {
                            this.createNewQuickReport$.next();
                        });
                        return false;
                    }
                    return true;
                }),
                filter(datePickerClosed => datePickerClosed),
                switchMapTo(this.userChartColors$.pipe(
                    take(1)
                )),
                tap(() => {
                    if (this.validateForm()) {
                        this.createNewQuickReport();
                    } else {
                        FocusHelper.focusFirstError(this.el.nativeElement);
                    }
                })
            ).subscribe()
        );
        // set default selected charts
        this.selectedCharts = this.options.filter(option => !option.multiSeries);
    }

    setReportType() {
        if (this.createSingleSeriesReport) {
            for (const chart of this.selectedCharts) {
                if (chart.multiSeries || this.selectedCharts.length !== this.multiOptionsCount) {
                    this.setCreateCustomReport(true, true);
                    break;
                }
            }
        } else {
            for (const chart of this.selectedCharts) {
                if (!chart.multiSeries || this.selectedCharts.length !== this.singleOptionsCount) {
                    this.setCreateCustomReport(true, true);
                    break;
                }
            }
        }
    }



    setCreateQuickReport(val: boolean) {
        this.createFromPrevious = null;
        this.createQuickReport = val;
    }

    setCreateFromPrevious(val: boolean) {
        this.createQuickReport = false;
        this.createFromPrevious = val;
        this.secondStep = false;
        // reset data from second step
        this.multiSeriesChartLabels = [];
        this.singleSeriesChartLabels = [];
    }

    setCreateCustomReport(val: boolean, autoHighlight: boolean) {
        if (!autoHighlight) {
            this.selectedCharts = [];
        }
        this.createSingleSeriesReport = null;
        this.createCustomReport = val;
    }

    setCreateSingleSeriesReport(val: boolean) {
        this.createCustomReport = false;
        this.createSingleSeriesReport = val;
        this.selectedCharts = this.options.filter(option => option.multiSeries !== val);
    }

    setQuickReportDialogState(val: boolean) {
        this.secondStep = val;
        this.setMessages();
    }

    onRowSelect(report): void {
        this.selectedReport = report;
    }

    cancelClicked(): void {
        this.closeDialog();
    }

    async createReport(): Promise<void> {
        if (this.createFromPrevious) {
            if (this.selectedReport !== null) {
                this.reportsService.cloneFromExistingReport(this.selectedReport.id);
                this.reportsService.navigateToCloneOfExistingReport(this.selectedReport.id);
                this.closeDialog();
            }
        } else {
            this.reportsService.makeReportClean();
            this.reportsService.setNewReport();
            this.reportsRoutingService.gotoReport();
            this.closeDialog();
        }
    }

    /**
     * Updates the list of suggestions (rowData) based on the filter text provided by the user
     */
    setFilteredReports(): void {
        this.rowData = this.reportListItems.filter(
            (reportListItem) => {
                return reportListItem.name.toLowerCase().indexOf(this.savedReportFilterText.toLowerCase()) !== -1;
            }
        );
    }


    /**
     * Creates a quick report
     */
    private createNewQuickReport(): void {
        this.reportsRoutingService.gotoReport(undefined, true);
        const selectedDateRange = this.series[0].dateRange;
        const reportCharts: ReportSeriesModel[][] = [];
        for (const selectedChart of this.selectedCharts) {
            let reportChart: ReportSeriesModel[];
            if (selectedChart.multiSeries) {
                reportChart = [];
                for (const multiSeriesChart of this.multiSeries) {
                    const multiSeriesChartClone = multiSeriesChart.clone(selectedChart.value as ReportBlockTypeEnum);
                    multiSeriesChartClone.chartType = selectedChart.value as ReportBlockTypeEnum;
                    if (this.hasDateRangeError) {
                        const dateRange = new ReportDateRangeModel();
                        dateRange.timezone = this.timezone;
                        multiSeriesChartClone.dateRange = dateRange;
                    } else {
                        multiSeriesChartClone.dateRange = selectedDateRange;
                    }
                    reportChart.push(multiSeriesChartClone);
                }
            } else {
                const singleSeriesChartClone = this.singleSeries[0].clone(selectedChart.value as ReportBlockTypeEnum);
                singleSeriesChartClone.chartType = selectedChart.value as ReportBlockTypeEnum;
                if (selectedDateRange.type === DateQueryTypeEnum.ALL && !singleSeriesChartClone.canUseAllDateRange()) {
                    const dateRange = new ReportDateRangeModel();
                    dateRange.timezone = this.timezone;
                    singleSeriesChartClone.dateRange = dateRange;
                } else {
                    singleSeriesChartClone.dateRange = selectedDateRange;
                }
                reportChart = [singleSeriesChartClone];
            }
            reportCharts.push(reportChart);
        }
        if(this.createSingleSeriesReport === true) {
            this.chartDimensionsMap.forEach((dimensions, chartType) => {
                const reportInfo = reportChartInfoMap.get(chartType);

                if(reportInfo){
                    const updatedReportInfo = {
                        ...reportInfo,
                        dimensions: dimensions
                    };
                    reportChartInfoMap.set(chartType, updatedReportInfo);
                }
            });
        }
        this.reportsService.createQuickReport(reportCharts, this.reportTitle, this.singleSeries, this.multiSeries);
        this.closeDialog();
        this.reportsService.makeReportClean();
    }

    checkDateRangeError(): void {
        if (this.series && this.series[0] && this.series[0].dateRange) {
            if (this.singleSeries && this.singleSeries[0]) {
                if (this.series[0].dateRange.type === DateQueryTypeEnum.ALL && !this.singleSeries[0].canUseAllDateRange()) {
                    this.hasDateRangeError = true;
                    return;
                } else {
                    this.singleSeries[0].dateRange = this.series[0].dateRange;
                }

                if (this.multiSeries && this.multiSeries.length) {
                    for (const series of this.multiSeries) {
                        if (this.series[0].dateRange.type === DateQueryTypeEnum.ALL && !series.canUseAllDateRange()) {
                            this.hasDateRangeError = true;
                            return;
                        } else {
                            series.dateRange =  this.series[0].dateRange;
                        }
                    }
                }
                this.hasDateRangeError = false;
            }

        }
    }

    // set date type options based on selected series types
    updateDateTypeOptions(): void {
        this.series = [ ...this.singleSeries, ...this.multiSeries ];
        const areAllSavedSources = this.series.every(s => reportSavedSourceTypes.includes(s.type));
        this.dateTypeOptions = areAllSavedSources ? savedSourceDateTypeOptions : defaultDateTypeOptions;
        this.dateRangeField.allowAllOption = areAllSavedSources;
    }

    private validateSingleSeries(): void {
        this.validSeries = true;
        this.validDateRange = true;

        if (!this.singleSeries || !this.singleSeries[0]) {
            this.validSeries = false;
            this.validDateRange = false;
        } else {
            if (!this.singleSeries[0].isValid()) {
                this.validSeries = false;
            }
            if (!this.singleSeries[0].dateRange.type) {
                this.validDateRange = false;
            }
        }
    }

    private validateMultiSeries(): void {
        this.validMultiSeries = true;
        this.validDateRange = true;

        for (let i = 0; i < this.multiSeries.length; i++) {
            if (!this.multiSeries[i].isValid()) {
                this.validMultiSeries = false;
            }
            if (!this.multiSeries[i].dateRange.type) {
                this.validDateRange = false;
            }
        }
    }

    /**
     * Checks the form data for errors
     */
    validateForm(): boolean {
        this.validReportTitle = true;
        if (!this.reportTitle || this.reportTitle.trim() === '') {
            this.validReportTitle = false;
        }

        if (this.singleSeriesChartSelected && !this.multiSeriesChartSelected) {
            this.validateSingleSeries();

        } else if (this.multiSeriesChartSelected && !this.singleSeriesChartSelected) {
            this.validateMultiSeries();

        } else if (this.singleSeriesChartSelected && this.multiSeriesChartSelected) {
            this.validateSingleSeries();
            this.validateMultiSeries();
        }
        return this.validReportTitle && this.validSeries && this.validMultiSeries && this.validDateRange;
    }

    singleSeriesChanged(singleSeries) {
        this.singleSeries = singleSeries;
        this.checkDateRangeError();
        this.updateDateTypeOptions();
        this.validateForm();
    }
    multiSeriesChanged(multipleSeries) {
        this.multiSeries = multipleSeries;
        this.checkDateRangeError();
        this.updateDateTypeOptions();
        this.validateForm();
    }

    setMessages() {
        // set tooltip messages
        for (const option of this.selectedCharts) {
            if (option.multiSeries) {
                this.multiSeriesChartLabels.push(option.label);
            } else {
                this.singleSeriesChartLabels.push(option.label);
            }
        }
        // set condition to display message when no charts selected
        if (this.multiSeriesChartLabels.length === 0) {
            this.multiSeriesChartSelected = false;
            this.singleSeriesChartSelected = true;
        }
        if (this.singleSeriesChartLabels.length === 0) {
            this.multiSeriesChartSelected = true;
            this.singleSeriesChartSelected = false;

        }
        if (this.multiSeriesChartLabels.length > 0 && this.singleSeriesChartLabels.length > 0) {
            this.multiSeriesChartSelected = true;
            this.singleSeriesChartSelected = true;
        }
        this.singleSeriesField.tooltipMessage = this.singleSeriesChartLabels;
        this.multiSeriesField.tooltipMessage = this.multiSeriesChartLabels;
    }


    /**
     * Checks to see if a calendar picker is currently open
     * @HACK this helps resolve an issue where if the date picker is open when saving the result of the dialog,
     *       then the date range does not get updated properly
     */
    isDatePickerOpen(): boolean {
        const picker: HTMLElement[] = Array.from(document.querySelectorAll('.ui-datepicker'));
        const isOpen = picker.find((element) => element.style.display !== 'none');
        return !!isOpen;
    }

    tabChange(index: any): void {
        this.selectedTabIndex = index;
        switch (index) {
            case ReportCreateDialogTabsEnum.QUICK_REPORT:
                this.setCreateQuickReport(true);
                break;
            case ReportCreateDialogTabsEnum.BLANK_REPORT:
                this.setCreateFromPrevious(false);
                break;
            case ReportCreateDialogTabsEnum.FROM_PREVIOUS:
                this.setCreateFromPrevious(true);
                break;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

}

export const coverageSummaryOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageSummary',
    ReportBlockTypeEnum.COVERAGE_SUMMARY,
    false,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const coverageTrendOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageTrend',
    ReportBlockTypeEnum.COVERAGE_OVER_TIME,
    false,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const coverageByMediaTypeOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageMediaType',
    ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN,
    false,
    [ userPermissionsEnum.advancedMonitoring ]
);
export const coverageSentimentOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageSentiment',
    ReportBlockTypeEnum.SENTIMENT_BREAKDOWN,
    false,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const coverageByRegionOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageRegion',
    ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME,
    false,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const topOutletsOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.topOutlets',
    ReportBlockTypeEnum.TOP_10_OUTLETS,
    false,
    [ userPermissionsEnum.advancedMonitoring ]
);
export const topAuthorsOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.topAuthors',
    ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME,
    false,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const topHashtagsOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.topHashtags',
    ReportBlockTypeEnum.TOP_HASHTAGS,
    false,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const coverageTrendComparisonOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageTrendComparison',
    ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON,
    true,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const coverageByMediaTypeComparisonOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageMediaTypeComparison',
    ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON,
    true,
    [ userPermissionsEnum.advancedMonitoring ]
);
export const coverageSentimentTrendOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageSentimentTrend',
    ReportBlockTypeEnum.SENTIMENT_TREND,
    false,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const coverageSentimentComparisonOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageSentimentComparison',
    ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON,
    true,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const shareOfVoiceOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.shareOfVoice',
    ReportBlockTypeEnum.SHARE_OF_VOICE,
    true,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const referralTrendOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.referralTrend',
    ReportBlockTypeEnum.REFERRAL_TREND,
    true,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);
export const coverageImpactOption: ChartOptionModel = new ChartOptionModel(
    'Reports.quickReport.chart.coverageImpact',
    ReportBlockTypeEnum.COVERAGE_IMPACT_TREND,
    true,
    [ userPermissionsEnum.advancedMonitoring, userPermissionsEnum.socialIntelligence ]
);

export const defaultChartOptionValuesModel: ChartOptionModel[] = [
    coverageSummaryOption,
    coverageTrendOption,
    coverageByMediaTypeOption,
    coverageSentimentOption,
    coverageSentimentTrendOption,
    coverageByRegionOption,
    topOutletsOption,
    topAuthorsOption,
    topHashtagsOption,
    coverageTrendComparisonOption,
    coverageByMediaTypeComparisonOption,
    coverageSentimentComparisonOption,
    shareOfVoiceOption,
];

export const advancedReportChartOptions: ChartOptionModel[] = [
    // @TODO
    referralTrendOption,
    coverageImpactOption
];
